"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "./cn";
import { I } from "./Icons";

export type CrmDropdownOption = {
  value: string;
  label: string;
  color?: string;
  initials?: string;
  icon?: React.ReactNode;
};

type Props = {
  value: string;
  options: CrmDropdownOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  "data-testid"?: string;
  renderValue?: (option: CrmDropdownOption | undefined) => React.ReactNode;
};

const MENU_MAX_HEIGHT = 260;
const MENU_ROW_HEIGHT = 36;
const MENU_GAP = 4;

export function CrmDropdown({
  value,
  options,
  onChange,
  placeholder = "Select…",
  disabled,
  id,
  className,
  renderValue,
  "data-testid": testId,
}: Props) {
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const menuHeight = Math.min(MENU_MAX_HEIGHT, (options.length || 1) * MENU_ROW_HEIGHT + 8);
      const below = window.innerHeight - rect.bottom - MENU_GAP;
      const above = rect.top - MENU_GAP;
      setDropUp(below < menuHeight && above > below);
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, options.length]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={ref} className={cn("relative", className)}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        disabled={disabled}
        data-testid={testId}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={cn(
          "flex h-10 w-full items-center gap-2 rounded-lg border bg-white px-2.5 text-[13px] text-[#101828] transition",
          open
            ? "border-brand ring-2 ring-brand/15"
            : "border-[#E4E7EC] hover:border-[#D0D5DD]",
          disabled && "cursor-not-allowed opacity-50",
        )}
      >
        <span className="flex min-w-0 flex-1 items-center gap-2 text-left">
          {renderValue
            ? renderValue(selected)
            : selected
              ? (
                <>
                  {selected.icon}
                  {selected.color !== undefined && (
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ background: selected.color }}
                    />
                  )}
                  {selected.initials !== undefined && (
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#F2F4F7] text-[9.5px] font-semibold text-[#475467]">
                      {selected.initials}
                    </span>
                  )}
                  <span className="truncate">{selected.label}</span>
                </>
              )
              : (
                <span className="truncate text-[#98A2B3]">{placeholder}</span>
              )}
        </span>
        <span
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-[#98A2B3] transition-transform",
            open && "rotate-180",
          )}
        >
          {I.chevronDown}
        </span>
      </button>

      {open && (
        <div
          role="listbox"
          className={cn(
            "absolute left-0 right-0 z-50 max-h-[260px] overflow-y-auto rounded-lg border border-[#E4E7EC] bg-white py-1 shadow-[0_8px_20px_rgba(16,24,40,0.08)]",
            dropUp ? "bottom-[calc(100%+4px)]" : "top-[calc(100%+4px)]",
          )}
        >
          {options.map((o) => {
            const active = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                className={cn(
                  "flex h-9 w-full items-center gap-2 px-2.5 text-left text-[13px] transition",
                  active
                    ? "bg-brand-subtle text-brand-cta"
                    : "text-[#101828] hover:bg-[#F9FAFB]",
                )}
              >
                {o.icon}
                {o.color !== undefined && (
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ background: o.color }}
                  />
                )}
                {o.initials !== undefined && (
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#F2F4F7] text-[9.5px] font-semibold text-[#475467]">
                    {o.initials}
                  </span>
                )}
                <span className="min-w-0 flex-1 truncate">{o.label}</span>
                {active && (
                  <span className="h-3.5 w-3.5 shrink-0 text-brand">{I.check}</span>
                )}
              </button>
            );
          })}
          {options.length === 0 && (
            <div className="px-2.5 py-3 text-center text-[12.5px] text-[#6B7280]">
              No options
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export const crmSelectTriggerClass =
  "h-10 rounded-lg border-[#E4E7EC] bg-white px-2.5 text-[13px] text-[#101828] shadow-none focus:border-brand focus:ring-2 focus:ring-brand/15 data-[state=open]:border-brand data-[state=open]:ring-2 data-[state=open]:ring-brand/15 [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:text-[#98A2B3] [&>svg]:opacity-100";

export const crmSelectContentClass =
  "rounded-xl border border-[#edf1f7] bg-white p-1 shadow-lg";

export const crmSelectItemClass =
  "cursor-pointer rounded-md py-2 pl-8 pr-2 text-[13px] text-[#101828] hover:bg-[#F9FAFB] focus:bg-brand-subtle focus:text-brand-cta data-[state=checked]:bg-brand-subtle data-[state=checked]:text-brand-cta";
